<template>
  <div>
    <template v-if="priceModal.check">
      <div class="check-container">
        {{ userData.name }}님,
        {{ priceModal.workType === "OPERATOR" ? "작업" : "검수" }} 단가가
        변경됐어요.<br />
        단가 동의 계약서에 서명 완료해 주세요.<br />
        서명 완료 전까지는 기존에 할당 받은 작업물만 수정할 수 있어요.
      </div>

      <div class="d-flex justify-center mt-6 mb-5">
        <CButton
          class="blue-primary"
          width="300px"
          height="50px"
          fontSize="14px"
          fontWeight="500"
          fill
          @click="checkClick"
        >
          서명하기
        </CButton>
      </div>
    </template>
    <template v-else>
      <div
        v-if="loading"
        style="height: 100%"
        class="d-flex justify-center align-center"
      >
        <CLottie :options="defaultOptions" :width="20" :height="20" />
      </div>
      <div v-else>
        <template v-if="!signState">
          <CDataTable
            :headers="headers"
            :datas="datas"
            :customSlot="customSlot"
            noDataText="조건에 맞는 항목이 없습니다."
          >
            <template #workType="{}">
              <label>
                {{ priceModal.workType === "OPERATOR" ? "작업" : "검수" }}
              </label>
            </template>

            <template #details="{ item }">
              <!-- 변경된 단가가 있을 때 -->
              <template v-if="changeData">
                <!-- 이전 unitType과 현재 unitType이 같을때 (화살표 없이 보여줌) -->
                <template v-if="item.typeCheck">
                  <div class="d-flex align-center">
                    <div
                      v-for="(detail, d_index) in item['previousPrices'].datas"
                      :key="d_index"
                      class="d-flex mb-1"
                      :class="{
                        'mt-3': d_index === 0,
                        'mb-3':
                          d_index === item['previousPrices'].datas.length - 1,
                      }"
                    >
                      <PriceClassItem :type="detail.type" />
                      <div class="d-flex">
                        <!-- 값의 변동이 있을 때 -->
                        <template
                          v-if="
                            !isNotChanged &&
                            detail.cost !==
                              item['currentPrices'].datas[d_index].cost
                          "
                        >
                          <label class="mr-2">
                            {{ `${detail.cost}원` }}
                          </label>
                          <label class="change-price">
                            -> &nbsp;{{
                              `${item["currentPrices"].datas[d_index].cost}원`
                            }}
                          </label>
                        </template>
                        <!-- 값의 변동이 없을 때 -->
                        <template v-else>
                          <label class="mr-2 change-price">
                            {{
                              `${item["currentPrices"].datas[d_index].cost}원 (변동 없음)`
                            }}
                          </label>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- 이전 unitType과 현재 unitType이 다를때 (화살표를 포함하여 보여줌) -->
                <template v-else>
                  <div class="d-flex align-center">
                    <template v-if="!isNotChanged">
                      <div v-if="item['previousPrices'].unitType === 'FILE'">
                        <div
                          v-for="(detail, d_index) in item['previousPrices']
                            .datas"
                          :key="d_index"
                          class="d-flex"
                        >
                          <PriceClassItem :type="detail.type" />
                          <label class="mr-1">
                            {{ `${detail.cost}원` }}
                          </label>
                        </div>
                      </div>
                      <div v-else>
                        <div
                          v-for="(detail, d_index) in item['previousPrices']
                            .datas"
                          :key="d_index"
                          class="d-flex mt-1 mb-1"
                          :class="{
                            'mt-3': d_index === 0,
                            'mb-3':
                              d_index ===
                              item['previousPrices'].datas.length - 1,
                          }"
                        >
                          <PriceClassItem :type="detail.type" />
                          <label class="mr-1">
                            {{ `${detail.cost}원` }}
                          </label>
                        </div>
                      </div>

                      <div class="arrow-label">
                        <label class="change-price"> &nbsp; -> &nbsp; </label>
                      </div>
                    </template>

                    <div v-if="item['currentPrices'].unitType === 'FILE'">
                      <div
                        v-for="(detail, d_index) in item['currentPrices'].datas"
                        :key="d_index"
                        class="d-flex"
                      >
                        <PriceClassItem :type="detail.type" />
                        <label class="change-price mr-1">
                          {{ `${detail.cost}원` }}
                        </label>
                      </div>
                    </div>
                    <div v-else>
                      <div
                        v-for="(detail, d_index) in item['currentPrices'].datas"
                        :key="d_index"
                        class="d-flex mt-1 mb-1"
                        :class="{
                          'mt-3': d_index === 0,
                          'mb-3':
                            d_index === item['currentPrices'].datas.length - 1,
                        }"
                      >
                        <PriceClassItem :type="detail.type" />
                        <label class="change-price mr-1">
                          {{ `${detail.cost}원` }}
                          <span v-if="isNotChanged">&nbsp;(변동 없음)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <!-- 변경된 단가가 없을 때 -->
              <template v-else>
                <!-- unitType이 FILE일 때 -->
                <div v-if="item.unitType === 'FILE'">
                  <div
                    v-for="(detail, d_index) in item.datas"
                    :key="d_index"
                    class="d-flex"
                  >
                    <PriceClassItem :type="detail.type" />

                    <label class="change-price mr-1">
                      {{ `${detail.cost}원` }}
                    </label>
                  </div>
                </div>
                <!-- unitType이 OBJECT일 때 -->
                <div v-else>
                  <div
                    v-for="(detail, d_index) in item.datas"
                    :key="d_index"
                    class="d-flex mt-1 mb-1"
                    :class="{
                      'mt-3': d_index === 0,
                      'mb-3': d_index === item.datas.length - 1,
                    }"
                  >
                    <PriceClassItem :type="detail.type" />

                    <label class="change-price mr-1">
                      {{ `${detail.cost}원` }}
                      <span v-if="isNotChanged">&nbsp;(변동 없음)</span>
                    </label>
                  </div>
                </div>
              </template>
            </template>
          </CDataTable>

          <div class="sign-modal-title mt-5">
            {{ userData.name }}님,
            <br />
            프로젝트 참여 계약서에 서명 완료해 주세요.
          </div>

          <template v-if="!contractState">
            <div class="sign-modal-body mb-6">
              <span class="sign-modal-span">
                참여비 지급 방법<br />
                매월 1일~말일 중 검수 완료된 건을 다음달 20일에 아래 계좌로
                입금<br />
              </span>
              <div class="d-flex">
                <span class="sign-modal-span">
                  <label class="label-font">{{
                    `${bankName} ${
                      userData.sensitive.account.accountNumber
                    } (예금주: ${nameMask(userData.name)})`
                  }}</label>
                </span>
                <span class="userinfo-update mt-1 ml-1" @click="userCertClick">
                  정보 수정하기
                </span>
              </div>
            </div>

            <div class="sign-modal-body mb-6">
              <span class="sign-modal-span">
                계약서는 카카오톡으로 발송됩니다 (카카오톡 수신 불가 시 문자로
                발송)<br />
                아래 연락처 확인 후

                <label class="label-font">[계약서 받기]</label> 버튼을
                눌러주세요.<br />
              </span>
              <div class="d-flex">
                <span class="sign-modal-span">
                  <label class="label-font">{{
                    userData.sensitive.phoneNo.replace(
                      /(\d{3})(\d{3,4})(\d{4})/,
                      "$1-$2-$3"
                    )
                  }}</label>
                </span>
                <span class="userinfo-update mt-1 ml-1" @click="userCertClick">
                  정보 수정하기
                </span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="sign-modal-body mb-6">
              <span class="sign-modal-span">
                계약서가 발송되었습니다.<br />
                발송 연락처:
                {{
                  userData.sensitive.phoneNo.replace(
                    /(\d{3})(\d{3,4})(\d{4})/,
                    "$1-$2-$3"
                  )
                }}
                <br />
                발송 일시: {{ formatDate }} <br />
                서명 완료 후 <label class="label-font">[새로고침]</label> 버튼을
                눌러주세요.<br />
              </span>
              <span class="sign-modal-span">
                <label class="label-font">
                  ※ 계약서를 재발송하려면 [다시 받기] 버튼을 눌러주세요.
                </label>
              </span>
            </div>
          </template>

          <div class="d-flex justify-center">
            <template v-if="!contractState">
              <CButton
                class="blue-primary"
                width="300px"
                height="50px"
                fontSize="14px"
                fontWeight="500"
                fill
                @click="contractClick"
                :disabled="contractSubmit"
              >
                <img
                  :src="
                    contractSubmit ? '/img/priec-submit.svg' : '/img/file.svg'
                  "
                  alt="prepend-icon"
                  draggable="false"
                  class="mr-1"
                />
                {{ contractSubmit ? "계약서 발송 중" : "계약서 받기" }}
              </CButton>
            </template>
            <template v-else>
              <template v-if="!refreshState">
                <CButton
                  class="blue-primary mr-2"
                  width="94px"
                  height="50px"
                  fontSize="14px"
                  fontWeight="500"
                  @click="reContractClick"
                >
                  다시 받기
                </CButton>
                <CButton
                  class="blue-primary"
                  width="200px"
                  height="50px"
                  fontSize="14px"
                  fontWeight="500"
                  fill
                  @click="refreshClick"
                >
                  새로고침
                </CButton>
              </template>
              <template v-else>
                <CButton
                  class="blue-primary"
                  width="300px"
                  height="50px"
                  fontSize="14px"
                  fontWeight="500"
                  fill
                  :disabled="true"
                >
                  <img
                    :src="
                      !retransmit
                        ? '/img/sign-pen.svg'
                        : '/img/priec-submit.svg'
                    "
                    alt="prepend-icon"
                    draggable="false"
                    class="mr-1"
                  />
                  {{ !retransmit ? "서명 확인 중" : "계약서 발송 중" }}
                </CButton>
              </template>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="sign-modal-title" v-html="resultTitle"></div>

          <div class="d-flex justify-center">
            <CButton
              class="blue-primary"
              width="300px"
              height="50px"
              fontSize="14px"
              fontWeight="500"
              fill
              @click="close"
            >
              확인
            </CButton>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import * as LottieData from "@/assets/lotties/loading-spinner-blue.json";
import CDataTable from "@/components/CDataTable.vue";
import CButton from "@/components/designSystem/CButton.vue";
import CLottie from "@/components/Lotties/CLottie.vue";
import { iNice, iPrice } from "@/services";
import PriceClassItem from "./PriceClassItem.vue";

export default {
  name: "PriceSign",
  components: {
    CButton,
    CDataTable,
    CLottie,
    PriceClassItem,
  },
  data() {
    return {
      loading: true,
      contractState: false,
      contractSubmit: false,
      refreshState: false,
      signState: false,
      resultTitle: "",
      bankName: "",
      submitState: "",
      interval: null,
      contractId: 0,
      headers: [
        {
          text: "업무",
          value: "workType",
          align: "center",
          width: "30%",
          sortable: false,
          children: [],
        },
        {
          text: "단가",
          value: "details",
          align: "left",
          width: "70%",
          sortable: false,
          children: [],
        },
      ],
      customSlot: ["workType", "details"],
      datas: [],
      changeData: false,
      defaultOptions: { animationData: LottieData },
      retransmit: false,
      isNotChanged: false,
      isObsolete: false,
    };
  },
  methods: {
    async contractClick() {
      this.contractSubmit = true;
      this.contractId = 0;

      const payload = {
        participantRole: this.priceModal.workType,
        taskId: parseInt(this.taskId),
      };
      const contractCreate = await iPrice.abbreviatedContract(payload); // 약식 계약서 생성 api
      if (!contractCreate) return;

      this.contractId = contractCreate.data.payload.id;
      this.interval = setInterval(() => this.contractCheck(), 1000);
    },
    async contractCheck(data) {
      const contractCheck = await iPrice.getContracts({
        exposeLevel: "MID",
        contractId: this.contractId,
      });
      if (!contractCheck) return;

      // TODO : SCHEDULED, ON_PROCESSING는 따로 기획에 나와 있지 않아서 모달 창만 close 시킴
      switch (contractCheck.data.payload.document.status) {
        case "ON_GOING": {
          if (data !== undefined) {
            this.refreshState = false;
            this.submitState = "ON_GOING";
          } else {
            clearInterval(this.interval);
            this.contractState = true;
          }
          return;
        }
        case "COMPLETED": {
          if (data !== undefined) {
            this.signState = true;
            this.resultTitle = `${this.userData.name}님,\n단가 동의 계약서에 서명 완료되었습니다.`;
            this.submitState = "COMPLETED";
          } else {
            clearInterval(this.interval);
            this.contractState = true;
          }
          return;
        }
        case "ABORTED": {
          if (data !== undefined) {
            this.signState = true;
            this.resultTitle = `서명이 취소되었습니다.`;
            this.submitState = "ABORTED";
          } else {
            clearInterval(this.interval);
            this.contractState = true;
          }
          return;
        }
        case "PROCESSING_FAILED": {
          if (data !== undefined) {
            this.signState = true;
            this.resultTitle = `처리 중 오류가 발생했습니다.\n처음부터 다시 진행해 주세요.`;
            this.submitState = "PROCESSING_FAILED";
          } else {
            clearInterval(this.interval);
            this.contractState = true;
          }
          return;
        }
        case "SCHEDULED":
        case "ON_PROCESSING": {
          clearInterval(this.interval);
          this.$emit("close");
          return;
        }
      }
    },
    async reContractClick() {
      this.refreshState = true;
      this.retransmit = true;

      const { data } = await iPrice.contractRetransmit(this.contractId);
      if (!data) return;

      const contractRetransmitStatus = data.payload.document.status;
      switch (contractRetransmitStatus) {
        case "ON_GOING":
          this.refreshState = false;
          this.submitState = "ON_GOING";
          break;
        case "COMPLETED":
          this.signState = true;
          this.resultTitle = `${this.userData.name}님,\n프로젝트 참여 계약서에 서명 완료되었습니다.`;
          this.submitState = "COMPLETED";
          break;
        case "ABORTED":
          this.refreshState = false;
          this.contractState = false;
          this.priceSettingTable();
          break;
        default:
          this.contractState = false;
          break;
      }
    },
    refreshClick() {
      this.refreshState = true;
      this.retransmit = false;
      this.contractCheck("refresh");
    },
    nameMask(name) {
      if (name.length <= 2) return name[0] + "*";
      return name[0] + "*".repeat(name.length - 2) + name[name.length - 1];
    },
    userCertClick() {
      this.$emit("close");
      window.location.href = "/profile";
    },
    async getBankData(data) {
      const result = await iNice.getBankName(data.bankCode);
      if (!result) return;

      data.bankName = result.data.payload.bankName;
      this.bankName = result.data.payload.bankName;
    },
    close() {
      this.$emit("close", {
        workType: this.priceModal.workType,
        submitState: this.submitState,
      });
    },

    async priceSettingTable() {
      this.loading = true;

      if (this.isObsolete) {
        await this.setObsoletePrice();
      } else {
        await this.setHistoryPrice();
      }

      this.loading = false;
    },

    /** OBSOLETE 계약서 변경내역 테이블 데이터 조립 */
    async setObsoletePrice() {
      // get previous price
      const myContracts = await iPrice.getContracts({
        exposeLevel: "ALL",
        contractId: this.contractId,
      });
      if (!myContracts) return;

      const prevData = myContracts.data.payload.document.prices.currentPrices;
      const prevDataDetails = prevData.map(({ type, value }) => ({
        type,
        cost: value,
      }));
      const prevUnitType = prevData.some((item) => item.unit === "FILE")
        ? "FILE"
        : "OBJECT";

      // get current price
      const groupDetail = await iPrice.getPriceGroupDetail(this.priceGroupId);
      if (!groupDetail) return;

      const type =
        this.priceModal.workType === "OPERATOR" ? "operate" : "inspect";
      const nextData = groupDetail.data.payload.prices[type];

      // set price data
      this.changeData = true;
      this.setDetailDatas({
        typeCheck: nextData.unitType === prevUnitType,
        previousPrices: { unitType: prevUnitType, datas: prevDataDetails },
        currentPrices: { unitType: nextData.unitType, datas: nextData.details },
      });
    },

    /** 단가 정보 가져오기 */
    async setHistoryPrice() {
      const { data } = await iPrice.getPriceHistories({
        projectId: this.projectId,
        taskId: this.taskId,
        workType:
          this.priceModal.workType === "OPERATOR" ? "OPERATE" : "INSPECT",
      });
      if (!data) return;

      const { histories } = data.payload;
      const prevData = histories[0].changes.prevPriceRecord;
      const nextData = histories[0].changes.nextPriceRecord;

      if (!prevData) {
        this.changeData = false;
        this.setDetailDatas({
          unitType: nextData.unitType,
          datas: nextData.details,
        });
      } else {
        this.changeData = true;
        this.setDetailDatas({
          typeCheck: nextData.unitType === prevData.unitType,
          previousPrices: {
            unitType: prevData.unitType,
            datas: prevData.details,
          },
          currentPrices: {
            unitType: nextData.unitType,
            datas: nextData.details,
          },
        });
      }
    },

    setDetailDatas(details) {
      this.datas = [{ details }];
    },

    async isContract() {
      const payload = {
        exposeLevel: "MID",
        priceGroupId: this.priceGroupId,
      };
      const { data: contractData } = await iPrice.getAbbreviatedContracts(
        payload
      );

      if (!contractData) return;
      if (contractData.code) {
        console.error(`[ERROR]: ${contractData.code}`);
        return;
      }

      // 계약서가 없을 시
      if (!contractData.payload.length) {
        this.isNotChanged = true;
        return;
      }

      const targetContract = contractData.payload.find(
        (item) => item.role === this.priceModal.workType
      );

      // 계약서는 있지만 자기 type에 맞는 계약서가 없을 시
      if (targetContract === undefined) return;

      this.contractId = targetContract.id;

      // 계약서에 단가가 변경 되었을 시
      switch (targetContract.status) {
        // CONTRACT_FAILED, CONTRACT_OBSOLETE, CONTRACT_ON_GOING 상태가 있지만 페이지 이동을 하지는 않음
        // 추후 페이지 이동이 있을 시 추가 하면 됨
        case "CONTRACT_OBSOLETE": {
          this.isObsolete = true;
          return;
        }
      }

      this.contractState = true;
      this.refreshState = false;
    },
    checkClick() {
      this.$store.commit("common/setPriceChange", {
        check: false,
      });
    },
  },
  computed: {
    formatDate() {
      let now = new Date();
      let year = now.getFullYear();
      let month = String(now.getMonth() + 1).padStart(2, "0");
      let day = String(now.getDate()).padStart(2, "0");
      let hour = String(now.getHours()).padStart(2, "0");
      let minute = String(now.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} 오후 ${
        hour > 12 ? (hour -= 12) : hour
      }:${minute}`;
    },
    userData() {
      const userData = this.$store.state.auth.userData;

      const renderUserData = {
        ...userData,
        SocialSecurityNumberCheck:
          userData.sensitive?.socialNumber === undefined ? false : true,
        BankNumberCheck:
          userData.sensitive?.account === undefined ||
          userData.sensitive.account.bankCode === null
            ? false
            : true,
        phoneNoCheck: userData.sensitive?.phoneNo === undefined ? false : true,
      };

      if (renderUserData.BankNumberCheck) {
        this.getBankData(userData.sensitive.account);
      }

      return renderUserData;
    },
    priceModal() {
      return this.$store.getters.common.priceModal;
    },
    taskId() {
      let taskId = this.$route.query.taskId;
      if (taskId === undefined) {
        const url = new URL(window.location.href);
        const splitData = url.pathname.split("/");

        taskId = splitData[splitData.length - 1];
      }
      return taskId;
    },
    projectId() {
      return this.$route.query.projectId;
    },
    priceGroupId() {
      return this.$store.state.dashboard.taskPriceGroupId;
    },
  },
  async created() {
    await this.isContract();
    await this.priceSettingTable();
  },
};
</script>

<style scoped>
.sign-modal-title {
  color: var(--mono-m-black01, #333);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24.3px;
  margin: 10px 0px 20px 0px;
}

.sign-modal-body {
  display: flex;
  padding: 30px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 20px;
  background: var(--mono-m-gray-04-mouse-over, #f5f5f5);
}

.sign-modal-span {
  color: var(--mono-m-black01, #333);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  line-height: 24.28px;
}

.label-font {
  font-size: 16px;
}

.userinfo-update {
  color: var(--mono-m-gray02-disabled, #d6d6d6);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 15.95px;
  text-decoration-line: underline;
  cursor: pointer;
}

.box-color {
  background: var(--sub-u-blue01, #b4e8f3);
}

.polygon-color {
  background: var(--class-card_purple-CC_p05, #afb2fe);
}

.point-group-color {
  background: var(--class-card_red-pink-CC_rp03, #f69f9f);
}

.point-single-color {
  background: var(--class-card_yellow-orange-CC_yo02, #fdd97e);
}

.attribute-color {
  background: var(--class-card_green-CC_g05, #7cdc96);
}
.file-color {
  background: #d3e2ff;
}

.change-price {
  color: #465afa;
}

.arrow-label {
  display: flex;
  justify-content: center;
  align-items: center;
}

.check-container {
  color: var(--mono-m-gray00, #666);
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 23.94px;
}
</style>
